import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "./components/app-layout";
import { NavUser } from "./components/nav-user";
import { ProtectedRoute } from "./components/protected-route";
import { routeConfig } from "./config/menu";
import { useAuth } from "./modules/authentication/context/AuthContext";
import LoginPage from "./modules/authentication/view/login-page";
import RegisterPage from "./modules/authentication/view/register-page";
import ResetPasswordPage from "./modules/authentication/view/reset-password";
import ForgotPasswordPage from "./modules/authentication/view/forgot-password";
import Home from "./modules/home/view";
import LiveKit from "./modules/livekit";
import ZaloCallbackPage from "./modules/authentication/view/zalo-callback";
import AccountView from "./modules/user/view/account";
import { SidebarProvider } from "./components/ui/sidebar";
import { Bell, Mail, PhoneCall, Search } from "lucide-react";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Separator } from "./components/ui/separator";
import { useNavigate } from "react-router";
import { companyInfo } from "./config/constants";
import { PermissionRoute } from "./components/permission-route";
import { getDefaultAuthorizedRoute } from "./modules/authentication/permissions";

// Toàn bộ tài khoản sau đăng nhập đều sử dụng khu vực quản trị IoT.
// Không chuyển tài khoản type "user" về trang Home cũ nữa.
function UserTypeRedirect({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// Layout đơn giản cho Home khi đã đăng nhập (có NavUser nhưng không có sidebar)
function HomeLayoutWithNav({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  return (
    <SidebarProvider>
      <div className="h-screen flex flex-col bg-sidebar w-full">
        <header className="flex h-16 shrink-0 items-center gap-4 bg-sidebar px-4 border-b">
          <div className="flex items-center gap-3 justify-center p-4" onClick={() => navigate("/")}>
            <div className="w-10 h-10 rounded-md bg-emerald-600 flex items-center justify-center shadow-lg transition-all duration-300">
              <span className="text-white font-bold text-sm">AB</span>
            </div>
            <div className="flex flex-col transition-all duration-300">
              <h1 className="font-bold text-lg tracking-wide text-gray-800">
                {companyInfo.name}
              </h1>
              <p className="text-xs text-gray-500 font-medium">
                {companyInfo.description}
              </p>
            </div>
          </div>
          <div className="flex flex-1 items-center gap-4">
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4 bg-gray-300"
            />
          </div>

          {/* Search Bar */}
          <div className="hidden md:flex relative w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Tìm kiếm..."
              className="pl-10 border-none shadow-none bg-white focus:bg-white transition-colors rounded-full"
            />
          </div>

          {/* Right side - Notifications and User */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                onClick={() => navigate("/sip-calls?newCall=1")}
                title="Tạo cuộc gọi mới"
                aria-label="Tạo cuộc gọi mới"
              >
                <PhoneCall className="w-5 h-5 text-emerald-600" />
              </button>
            </div>

            {/* Notifications */}
            <div className="relative">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                <Bell className="w-5 h-5 text-gray-600" />
                <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                  1
                </Badge>
              </button>
            </div>

            {/* Messages */}
            <div className="relative">
              <button
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => navigate("/messages")}
              >
                <Mail className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* User Menu */}
            <NavUser />
          </div>
        </header>
        <main className="flex-1 bg-sidebar p-4 pt-0 overflow-y-auto">
          <div className="min-h-full w-full bg-white p-8 rounded-lg">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

// Component wrapper cho Home - hiển thị với layout có NavUser nếu đã đăng nhập
function HomeWrapper() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <HomeLayoutWithNav>
        <Home />
      </HomeLayoutWithNav>
    );
  }

  return <Home />;
}

// Component wrapper cho Account - hiển thị với HomeLayoutWithNav nếu user type 'user'
function AccountWrapper() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user?.type === "user") {
    return (
      <ProtectedRoute>
        <HomeLayoutWithNav>
          <AccountView />
        </HomeLayoutWithNav>
      </ProtectedRoute>
    );
  }

  // Admin: sử dụng AppLayout (sẽ được xử lý trong ProtectedRoute)
  // Redirect đến route trong ProtectedRoute
  return <Navigate to="/account-admin" replace />;
}

export default function Router() {
  const { isAuthenticated, user } = useAuth();

  const getDefaultRoute = () => getDefaultAuthorizedRoute(user);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={getDefaultRoute()} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route path="/zalocallback" element={<ZaloCallbackPage />} />
      <Route
        path="/register"
        element={
          isAuthenticated ? (
            <Navigate to={getDefaultRoute()} replace />
          ) : (
            <RegisterPage />
          )
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/meeting/:meetingId/:meetingCode" element={<LiveKit />} />
      {/* Home route - accessible for both authenticated and unauthenticated users */}
      <Route
        path="/home"
        element={isAuthenticated ? <Navigate to="/monitoring" replace /> : <HomeWrapper />}
      />
      {/* Account route - hiển thị với HomeLayoutWithNav cho user type 'user' */}
      <Route path="/account" element={<AccountWrapper />} />
      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        {/* Route /account cho admin - sử dụng AppLayout với Outlet */}
        <Route path="/account-admin" element={<AccountView />} />
        {routeConfig
          .filter((route) => route.path !== "/account") // Loại bỏ /account khỏi routeConfig vì đã xử lý riêng
          .map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <PermissionRoute permission={route.permission}>
                  <UserTypeRedirect>
                    <route.component />
                  </UserTypeRedirect>
                </PermissionRoute>
              }
            />
          ))}
      </Route>
      {/* Catch all route - must be last */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
